<template>
  <div class="flex flex-col">
    <!-- Warning when no media access is available -->
    <div
      v-if="!orgStore.hasAnyMediaAccess"
      class="bg-warning-50 border border-warning-200 rounded-md p-3 mb-2"
    >
      <div class="flex">
        <div class="flex-shrink-0">
          <font-awesome-icon
            :icon="['fas', 'exclamation-triangle']"
            class="h-4 w-4 text-warning-400"
            aria-hidden="true"
          />
        </div>
        <div class="ml-3">
          <p class="text-sm text-warning-800">
            Your account currently doesn't have access to any media. Please
            contact your administrator to enable media access.
          </p>
        </div>
      </div>
    </div>

    <div
      v-show="orgStore.hasDigitalMediaAccess"
      data-intercom-target="Digital media toggle"
      class="flex gap-x-2 items-center my-2"
    >
      <ToggleSwitch
        :switch-value="searchFilterStore.getCurrentSearchFilter.web_media_type"
        @switch-clicked="onSwitchClicked('web')"
      />
      <div class="w-[18px] h-[18px] flex items-center justify-center">
        <font-awesome-icon
          class="icon"
          :icon="['far', 'globe']"
          style="width: 15px; height: 15px"
        />
      </div>
      <div class="flex items-center">
        <span class="font-body-2">{{ $t("appliedFilters.digital") }}</span>
      </div>
    </div>

    <div
      v-show="orgStore.hasPrintMediaAccess"
      data-intercom-target="Print media toggle"
      class="flex gap-x-2 items-center my-2"
    >
      <ToggleSwitch
        :switch-value="
          searchFilterStore.getCurrentSearchFilter.print_media_type
        "
        @switch-clicked="onSwitchClicked('print')"
      />
      <div class="w-[18px] h-[18px] flex items-center justify-center">
        <font-awesome-icon
          class="icon"
          :icon="['far', 'newspaper']"
          style="width: 15px; height: 15px"
        />
      </div>
      <div class="flex items-center">
        <span class="font-body-2">{{ $t("appliedFilters.print") }}</span>
      </div>
    </div>

    <div
      v-show="orgStore.hasBroadcastMediaAccess"
      data-intercom-target="TV media toggle"
      class="flex gap-x-2 items-center my-2"
    >
      <ToggleSwitch
        :switch-value="searchFilterStore.getCurrentSearchFilter.tv_media_type"
        @switch-clicked="onSwitchClicked('tv')"
      />
      <div class="w-[18px] h-[18px] flex items-center justify-center">
        <font-awesome-icon
          class="icon"
          :icon="['far', 'tv-retro']"
          style="width: 15px; height: 15px"
        />
      </div>
      <div class="flex items-center">
        <span class="font-body-2">{{ $t("appliedFilters.television") }}</span>
      </div>
    </div>
    <div
      v-show="orgStore.hasBroadcastMediaAccess"
      data-intercom-target="Radio media toggle"
      class="flex gap-x-2 items-center my-2"
    >
      <ToggleSwitch
        :switch-value="
          searchFilterStore.getCurrentSearchFilter.radio_media_type
        "
        @switch-clicked="onSwitchClicked('radio')"
      />
      <div class="w-[18px] h-[18px] flex items-center justify-center">
        <font-awesome-icon
          class="icon"
          :icon="['far', 'radio']"
          style="width: 15px; height: 15px"
        />
      </div>
      <div class="flex items-center">
        <span class="font-body-2">{{ $t("appliedFilters.radio") }}</span>
      </div>
    </div>
    <div
      v-show="orgStore.hasPodcastAccess"
      data-intercom-target="Podcast media toggle"
      class="flex gap-x-2 items-center my-2"
    >
      <ToggleSwitch
        :switch-value="
          searchFilterStore.getCurrentSearchFilter.podcast_media_type
        "
        @switch-clicked="onSwitchClicked('podcast')"
      />
      <div class="w-[18px] h-[18px] flex items-center justify-center">
        <font-awesome-icon
          class="icon"
          :icon="['fas', 'podcast']"
          style="width: 15px; height: 15px"
        />
      </div>
      <div class="flex items-center">
        <span class="font-body-2">{{ $t("appliedFilters.podcast") }}</span>
      </div>
    </div>
    <div
      v-show="orgStore.hasSocialMediaAccess"
      class="flex items-center justify-between"
    >
      <div
        data-intercom-target="Social media toggle"
        class="flex gap-x-2 items-center my-2"
      >
        <ToggleSwitch
          :switch-value="
            searchFilterStore.getCurrentSearchFilter.social_media_type
          "
          @switch-clicked="onSwitchClicked('social')"
        />
        <div class="w-[18px] h-[18px] flex items-center justify-center">
          <font-awesome-icon
            class="icon"
            :icon="['far', 'share-nodes']"
            style="width: 15px; height: 15px"
          />
        </div>
        <div class="flex items-center">
          <span class="font-body-2">{{ $t("appliedFilters.social") }}</span>
        </div>
      </div>
      <el-tooltip
        placement="left"
        popper-class="text-white text-sm font-normal font-['Inter'] leading-tight !important font-body-3"
      >
        <template #content>
          <b>{{ $t("appliedFilters.mostRelevantSocialMentions") }}</b>
          <p>{{ $t("appliedFilters.lowReachPostsAreExcludedByDefault") }}</p>
          <p>
            {{
              $t("appliedFilters.turnOffSocialMentionsWithLowReachToViewAll")
            }}
          </p>
        </template>
        <font-awesome-icon
          class="w-4 h-4 rounded-full text-secondary-600 focus:outline-none"
          :icon="['far', 'info-circle']"
        />
      </el-tooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import ToggleSwitch from "~/components/shared/toggle-switch/ToggleSwitch.vue";
import { useOrgStore } from "@/store/org";
import { useSearchFiltersStore } from "~/store/searchFilter";

const orgStore = useOrgStore();
const searchFilterStore = useSearchFiltersStore();

const emit = defineEmits(["performUpdate"]);

const onSwitchClicked = (type: string) => {
  switch (type) {
    case "web":
      searchFilterStore.getCurrentSearchFilter.web_media_type =
        !searchFilterStore.getCurrentSearchFilter.web_media_type;
      break;
    case "print":
      searchFilterStore.getCurrentSearchFilter.print_media_type =
        !searchFilterStore.getCurrentSearchFilter.print_media_type;
      break;
    case "tv":
      searchFilterStore.getCurrentSearchFilter.tv_media_type =
        !searchFilterStore.getCurrentSearchFilter.tv_media_type;
      break;
    case "radio":
      searchFilterStore.getCurrentSearchFilter.radio_media_type =
        !searchFilterStore.getCurrentSearchFilter.radio_media_type;
      break;
    case "social":
      searchFilterStore.getCurrentSearchFilter.social_media_type =
        !searchFilterStore.getCurrentSearchFilter.social_media_type;
      searchFilterStore.getCurrentSearchFilter.facebook_media_type =
        searchFilterStore.getCurrentSearchFilter.social_media_type;
      searchFilterStore.getCurrentSearchFilter.instagram_media_type =
        searchFilterStore.getCurrentSearchFilter.social_media_type;
      searchFilterStore.getCurrentSearchFilter.twitter_media_type =
        searchFilterStore.getCurrentSearchFilter.social_media_type;
      searchFilterStore.getCurrentSearchFilter.youtube_media_type =
        searchFilterStore.getCurrentSearchFilter.social_media_type;
      if (!searchFilterStore.getCurrentSearchFilter.social_media_type) {
        searchFilterStore.getCurrentSearchFilter.verified_accounts_only = false;
        searchFilterStore.getCurrentSearchFilter.hide_repost = false;
        searchFilterStore.getCurrentSearchFilter.include_comments = false;
      }
      break;
    case "podcast":
      searchFilterStore.getCurrentSearchFilter.podcast_media_type =
        !searchFilterStore.getCurrentSearchFilter.podcast_media_type;
      break;
    default:
      break;
  }
  emit("performUpdate");
};
</script>

<style scoped>
.text {
  @apply text-gray-950 text-sm font-medium font-['Inter'] leading-tight;
}
</style>
